import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Grid,
} from '@mui/material';
import RemoveIcon from '@mui/icons-material/Remove';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import CarIcon from '@mui/icons-material/DirectionsCar';
import TruckIcon from '@mui/icons-material/LocalShipping';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';

// ✅ IMPORT CUSTOM ICONS
import trendUp from "../assets/icons/trend.png";
import trendDown from "../assets/icons/business.png";

// ✅ REORDERED to match screenshot: Row 1 (A, C), Row 2 (B, E)
const COECategories = ['A', 'C', 'B', 'E'];

// Helper function for styling and details
const getCategoryDetails = (category: string) => {
  switch (category) {
    case 'A':
      return {
        name: 'CAT A',
        description: 'Cars ≤ 1600cc & ≤ 130bhp',
        Icon: CarIcon,
        bgColor: '#ffebee', // Light Red
        iconColor: '#d32f2f',
      };
    case 'B':
      return {
        name: 'CAT B',
        description: 'Cars > 1600cc OR > 130bhp',
        Icon: CarIcon,
        bgColor: '#e3f2fd', // Light Blue
        iconColor: '#1976d2',
      };
    case 'C':
      return {
        name: 'CAT C',
        description: 'Goods vehicles, commercial vans, lorries & buses',
        Icon: TruckIcon,
        bgColor: '#e8f5e9', // Light Green
        iconColor: '#388e3c',
      };
    case 'E':
      return {
        name: 'CAT E',
        description: 'Open Category (Usually high-performance cars)',
        Icon: DirectionsBusIcon,
        bgColor: '#fff3e0', // Light Orange
        iconColor: '#f57c00',
      };
    default:
      return {
        name: `CAT ${category}`,
        description: '',
        Icon: CarIcon,
        bgColor: '#f5f5f5',
        iconColor: '#757575'
      };
  }
};

// --- Sub-Component for a Grid Item ---
const CategoryGridItem = ({ category, premium, trend, color }: any) => {
  const details = getCategoryDetails(category);
  const IconComponent = details.Icon;

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, height: '100%' }}>
      {/* Icon Circle */}
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          backgroundColor: details.bgColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <IconComponent sx={{ fontSize: 26, color: details.iconColor }} />
      </Box>

      {/* Text Content */}
      <Box>
        {/* Header Row: Category Name + Price */}
        <Box display="flex" alignItems="baseline" flexWrap="wrap" gap={1}>
          <Typography variant="subtitle1" fontWeight={800} color="text.primary">
            {details.name}
          </Typography>

          {/* Price displayed next to name to keep it compact but visible */}
          <Typography variant="body2" fontWeight={700} sx={{ color: color }}>
            {premium}
          </Typography>
          <Box component="span" sx={{ display: 'inline-flex', verticalAlign: 'middle' }}>
            {trend}
          </Box>
        </Box>

        {/* Description */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: 'block',
            lineHeight: 1.3,
            fontSize: '0.75rem',
            mt: 0.5
          }}
        >
          {details.description}
        </Typography>
      </Box>
    </Box>
  );
};

const COEResultsCard = () => {
  const [latestGroup, setLatestGroup] = useState<any[]>([]);
  const [previousGroup, setPreviousGroup] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // --- Data Fetching Logic ---
  const fetchCOEData = async () => {
    setLoading(true);
    try {
      const response = await axios.get<any>(
        'https://api.workhorse.com.sg/Vehicle/VehicleApiCall',
        {
          headers: { 'Content-Type': 'application/json', 'Api-Key': 'FrontEndKey' },
        }
      );

      const rawList = response?.data?.responseData?.vehicleApiCallList || [];

      // Grouping Logic
      const grouped: Record<string, Record<string, any[]>> = {};
      rawList.forEach((item: any) => {
        if (!grouped[item.month]) grouped[item.month] = {};
        if (!grouped[item.month][item.biddingNo]) grouped[item.month][item.biddingNo] = [];
        grouped[item.month][item.biddingNo].push(item);
      });

      const sortedMonths = Object.keys(grouped).sort((a, b) =>
        new Date(b + '-01').getTime() - new Date(a + '-01').getTime()
      );

      let latest: any[] = [];
      let previous: any[] = [];

      // Find latest and previous bidding
      for (let i = 0; i < sortedMonths.length; i++) {
        const month = sortedMonths[i];
        const monthGroup = grouped[month];
        if (monthGroup['2']) {
          latest = monthGroup['2'];
          previous = monthGroup['1'] || (grouped[sortedMonths[i + 1]] ? (grouped[sortedMonths[i + 1]]['2'] || grouped[sortedMonths[i + 1]]['1']) : []);
          break;
        } else if (monthGroup['1']) {
          latest = monthGroup['1'];
          previous = grouped[sortedMonths[i + 1]] ? (grouped[sortedMonths[i + 1]]['2'] || grouped[sortedMonths[i + 1]]['1']) : [];
          break;
        }
      }

      setLatestGroup(latest);
      setPreviousGroup(previous);
    } catch (err) {
      console.error('Error fetching COE data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCOEData();
  }, []);

  // --- Price and Trend Logic ---
  const getPremiumAndTrend = (category: string) => {
    const current = latestGroup.find((i) => i.vehicleClass === `Category ${category}`);
    const prev = previousGroup.find((i) => i.vehicleClass === `Category ${category}`);

    if (!current) return { premium: '-', trend: null, color: 'text.primary' };

    const premiumVal = Number(current.premium);
    const formatted = `$${premiumVal.toLocaleString()}`;

    if (!prev) return { premium: formatted, trend: null, color: 'text.primary' };

    const prevPremium = Number(prev.premium);

    if (premiumVal > prevPremium) {
      return {
        premium: formatted,
        trend: <img src={trendUp} alt="up" style={{ width: 16, marginLeft: 4 }} />,
        color: '#d32f2f', // Red
      };
    }
    if (premiumVal < prevPremium) {
      return {
        premium: formatted,
        trend: <img src={trendDown} alt="down" style={{ width: 16, marginLeft: 4 }} />,
        color: '#388e3c', // Green
      };
    }

    return { premium: formatted, trend: <RemoveIcon sx={{ fontSize: 14, ml: 0.5 }} />, color: 'text.primary' };
  };

  const latestData = latestGroup[0];
  const dateLabel = latestData
    ? `${new Date(latestData.month + '-01').toLocaleString('default', { month: 'long' })} ${latestData.month.split('-')[0]}`
    : 'Loading...';

  const biddingLabel = latestData
    ? `${latestData.biddingNo}${latestData.biddingNo === '1' ? 'st' : 'nd'} Bidding`
    : '';

  const fullLabel = latestData ? `${dateLabel} / ${biddingLabel}` : 'Loading Data...';

  return (
    <Box
      component="a"
      href="/coe-dashboard"
      sx={{ maxWidth: 700, mx: 'auto', fontFamily: 'sans-serif' }}>
      {/* Top Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} px={0.5}>
        <Typography variant="h6" fontWeight={800}>
          COE Results
        </Typography>
        <Box
          component="a"
          href="/coe-dashboard"
          display="flex"
          alignItems="center"
          sx={{ textDecoration: 'none', color: 'text.secondary', cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
        >
          <Typography variant="caption" fontWeight={700} fontSize={12}>
            More details
          </Typography>
          <KeyboardArrowRightIcon fontSize="small" />
        </Box>
      </Box>

      {/* Main Card */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          p: 4,
          backgroundColor: '#f6f7f9', // Slightly grey background to match image card style
          border: '1px solid #eeeeee',
        }}
      >
        <Typography variant="subtitle1" fontWeight={800} mb={3} color="text.primary">
          {fullLabel}
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          // ✅ GRID LAYOUT: 2 columns on small screens and up
          <Grid container rowSpacing={4} columnSpacing={2}>
            {COECategories.map((cat) => {
              const { premium, trend, color } = getPremiumAndTrend(cat);
              return (
                <Grid size={{ xs: 12, sm: 6 }} key={cat}>
                  <CategoryGridItem
                    category={cat}
                    premium={premium}
                    trend={trend}
                    color={color}
                  />
                </Grid>
              );
            })}
          </Grid>
        )}
      </Paper>
    </Box>
  );
};

export default COEResultsCard;